import type { ScenarioSpec } from "./types";

/**
 * Walkable open-air pool deck — assembled from the purchased Swimming Pool
 * Environment Pack (FBX + PBR textures) into one browser-ready GLB. Free-roam
 * practice room only: intro splash + WASD walk. Not linked from the intro
 * library page yet and not seeded into Cyberfrog `_system`.
 *
 * The model is raw PBR with NO baked lighting, so the daylight has to come from
 * the renderer: `outdoor` supplies a blue sky cubemap, sun, shadows, ground and
 * a grass-only palm belt; `water` is a refractive physical surface over a tiled
 * floor. Full trees stay on the lawn. Terrace pots are pack planters with a
 * small palm in each. Both outdoor/water are opt-in, so no other course is affected.
 *
 * Geometry (metres, Y-up after glTF export): deck spans X ±15 / Z ±13, the pool
 * opening is X ±5 / Z ±8 with its rim at Y=0 and the basin 1.8 m below. There
 * are no walls — containment comes from floor-gating, which only permits a step
 * where deck mesh is actually underfoot, so the pool and the lawn are both
 * out of bounds automatically.
 */

const POOL_HALF_X = 5.0;
const POOL_HALF_Z = 8.0;
const DECK_HALF_Z = 13.0;
/** Waterline, matching WATER_Z in ops/_assemble_pool_glb.py. */
const WATER_Y = -0.06;
/** Column centres along the far deck, mirroring the assembly script. */
const COLUMN_XS = [-12.0, -7.2, -2.4, 2.4, 7.2, 12.0];
const COLUMN_Z = -(DECK_HALF_Z - 1.2);
const COLUMN_R = 0.6;

export const poolWalkableScenario: ScenarioSpec = {
  version: 1,
  id: "pool-walkable",
  title: "Walkable Pool",
  topic: "Pool deck opening — iPad on the lounger",
  walkHudMinimal: true,
  language: "en",
  ambientUrl: "/cdn/sim/pool-walkable/tropical-island-aurosonmusic.mp3",
  ambientVolume: 0.06,
  music: "none",
  walkable: true,
  riskMeter: false,
  passThreshold: 0,

  scene: {
    environment: {
      gltfUrl: "/cdn/sim/pool-walkable/pool.glb",
      gltfScale: 1,
      gltfPosition: [0, 0, 0],
      gltfRotationDeg: [0, 0, 0],
      // Solid props only. The perimeter is handled by floor-gating, not by a
      // hand-drawn rectangle.
      gltfColliders: [
        ...COLUMN_XS.map((x) => ({
          min: [x - COLUMN_R, 0, COLUMN_Z - COLUMN_R] as [number, number, number],
          max: [x + COLUMN_R, 4.4, COLUMN_Z + COLUMN_R] as [number, number, number],
        })),
        // Lifeguard station on the east deck.
        { min: [12.0, 0, 4.4], max: [13.2, 2.4, 5.6] },
        // Jacuzzi basin, south-east deck.
        { min: [8.2, 0, 8.2], max: [11.8, 1.3, 11.8] },
        // Dolphin statue, south-west deck.
        { min: [-10.9, 0, 9.1], max: [-9.1, 2.6, 10.9] },
        // Second lifeguard lookout (west deck) — the mesh includes its chair.
        { min: [-13.4, 0, -6.0], max: [-11.8, 2.8, -4.4] },
      ],
      staticGltfProps: [
        {
          url: "/cdn/sim/pool-walkable/people-v13.glb",
          position: [0, 0, 0],
        },
      ],
      outdoor: {
        sunElevationDeg: 32,
        sunAzimuthDeg: 125,
        sunIntensity: 2.7,
        shadows: true,
        shadowRadius: 20,
        ground: { color: "#4f7a38", size: 90, y: -0.19 },
        palms: {
          count: 52,
          radius: 18,
          // Must match Decoration/Planter.fbx placements in _assemble_pool_glb.py
          // (Blender (x,y) -> glTF (x, -y)).
          pots: [
            [-13.6, 11.6],
            [13.6, 11.6],
            [-13.6, -11.6],
            [13.6, -11.6],
            [-13.6, 0],
            [13.6, 0],
            [-6.4, 10.6],
            [-12.2, 6.4],
            [-12.2, -2.2],
            [12.2, 6.4],
            [6.2, 10.6],
          ],
        },
      },
      water: {
        center: [0, WATER_Y, 0],
        width: POOL_HALF_X * 2 - 0.3,
        depth: POOL_HALF_Z * 2 - 0.3,
        color: "#0f6e9c",
        distortionScale: 0.52,
        speed: 0.7,
        floorY: -1.62,
      },
      waters: [
        {
          // Kids / jacuzzi on the south-east deck. Blender (10,-10) -> glTF (10, 10).
          shape: "circle",
          center: [10.0, WATER_Y, 10.0],
          width: 3.4,
          depth: 3.4,
          color: "#0c5f8a",
          distortionScale: 0.7,
          speed: 0.9,
          floorY: WATER_Y - 0.7,
        },
      ],
      enableSplat: false,
      enablePano: false,
      // Stamp-only (gltfUrl wins in Environment3D). Scopes --scr-* for the iPad overlay.
      procedural: "pool_walkable",
      // Re-measured off pool_f10 by material, not by loose part
      // (ops/_probe_f10_materials.py — the mesh is triangle soup, so a loose-part
      // split returns single tris and finds her forearm instead of the tablet).
      // Material F001_White is exactly two triangles: a 0.13 x 0.20 m portrait
      // plate standing 15 deg off vertical whose normal is (0, 0.259, -0.966),
      // i.e. tilted up and pointing back at her face. Values below carry a small
      // bleed over the measured plate so the panel covers the bezel edge.
      //
      // THE CONSTRAINT THAT DRIVES THE CAMERA: her head sits only 0.35 m in front
      // of the tablet, on the same side the screen faces. Any push-in straight
      // down the normal ends up inside her head, and one merely near it puts her
      // head across the sightline — the overlay is DOM with no depth test, so the
      // login then paints on top of her hair. See the waypoints below.
      screenQuad: {
        center: [-5.246, 0.866, 4.194],
        width: 0.145,
        height: 0.212,
        rotationDeg: 180,
        tiltDeg: -15,
        maxDistance: 4,
      },
      backdropColor: "#6eb6e8",
      exposure: 1.05,
      splat: {},
    },
    props: [],
    waypoints: [
      {
        // South rim of the kids/jacuzzi pool, looking into the water.
        id: "wp_kids",
        position: [10.0, 1.55, 12.5],
        target: [10.0, 0.2, 10.0],
        fov: 58,
        durationS: 2.6,
      },
      {
        // Standing behind her on the deck tiles, at eye height. She sits facing
        // +Z (head at z 3.85, legs out to z 5.18), so this is squarely at her
        // back — the whole lounger and the pool beyond read in one shot.
        id: "wp_behind",
        position: [-6.1, 1.95, 1.6],
        target: [-5.35, 1.05, 3.9],
        fov: 48,
        durationS: 4.2,
      },
      {
        // Straight in along the same line. Her head is centre-frame here on
        // purpose: this is the "coming up behind her" moment, and no overlay is
        // drawn during a camera beat.
        id: "wp_behind_close",
        position: [-5.55, 1.7, 2.35],
        target: [-5.3, 0.95, 4.05],
        fov: 38,
        durationS: 3.0,
      },
      {
        // Arc out over the water to clear her head. Everything from here on is
        // solved against the head sphere at (-5.252, 0.965, 3.852), r ~0.11: this
        // sightline passes 0.16 m clear of it.
        id: "wp_shoulder",
        position: [-4.8, 1.34, 3.2],
        target: [-5.25, 0.88, 4.194],
        fov: 32,
        durationS: 2.4,
      },
      {
        // Final: 0.62 m from the plate, on the screen normal yawed 34 deg toward
        // the pool. Straight down the normal would be inside her head; 34 deg
        // buys 0.20 m of clearance and still keeps the plate at ~80% of viewport
        // height (0.212 m tall seen through a 24 deg vertical fov), with her head
        // 0.20 m off-axis where the frame is only 0.12 m wide — so she is out of
        // shot entirely. Login is withheld until this beat ends.
        id: "wp_ipad_fill",
        position: [-4.911, 1.027, 3.697],
        target: [-5.246, 0.866, 4.194],
        fov: 24,
        durationS: 2.6,
      },
      {
        // After the iPad beat: back on the west deck, free walk.
        id: "wp_walk",
        position: [-8.4, 1.65, 4.4],
        target: [0, 0.3, 2],
        fov: 62,
        durationS: 1.8,
      },
    ],
    initialWaypointId: "wp_kids",
  },

  devices: [
    {
      id: "pool_ipad",
      kind: "pooltablet",
      config: {
        lockTime: "11:42",
        lockDate: "Sunday, 16 August",
        ownerName: "Personal iPad",
        continueLabel: "Continue",
      },
    },
  ],

  voiceAttackers: {
    walk_poc: {
      persona: {
        name: "PoC",
        role: "Walk",
        pretext: "it_support",
        language: "en",
        voiceId: "onyx",
      },
      goal: "Walk the pool deck.",
      opening: "",
      allowedTopics: [],
      scoringEvents: [],
      forbidden: [],
      maxDurationS: 0,
    },
  },

  initialBeatId: "b_intro",

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Walkable Pool",
      kicker: "Opening \u00b7 Pool deck",
      highlight: "kids' pool",
      ctaLabel: "Start",
      guide: {
        title: "Walkable Pool",
        body:
          "We start at the small kids' pool, then move to the woman on the lounger " +
          "with the iPad. After a close look at the screen you can walk the deck freely.",
      },
    },
    {
      id: "b_kids",
      type: "camera",
      waypointId: "wp_kids",
      guide: {
        title: "Kids' pool",
        body: "The small round pool on the south-east deck.",
      },
    },
    {
      id: "b_behind",
      type: "camera",
      waypointId: "wp_behind",
      walkSfx: true,
      guide: {
        title: "The lounger",
        body: "Someone on the west rim is using a tablet.",
      },
    },
    {
      id: "b_behind_close",
      type: "camera",
      waypointId: "wp_behind_close",
      guide: {
        title: "Over her shoulder",
        body: "Coming up behind her for a look at the screen.",
      },
    },
    {
      id: "b_shoulder",
      type: "camera",
      waypointId: "wp_shoulder",
      guide: {
        title: "The screen",
        body: "The iPad is angled up toward her face.",
      },
    },
    {
      id: "b_zoom",
      type: "camera",
      waypointId: "wp_ipad_fill",
      guide: {
        title: "Close up",
        body: "Moving in until the white screen fills the view.",
      },
    },
    {
      id: "b_ipad",
      type: "device",
      deviceId: "pool_ipad",
      completeOn: { action: "continue" },
      guide: {
        title: "Switch it on",
        body: "Tap the power button to turn the iPad on.",
      },
    },
    {
      id: "b_walk",
      type: "camera",
      waypointId: "wp_walk",
      hold: true,
      guide: {
        title: "Walk the deck",
        body:
          "W/A/S/D (or the on-screen pad) to move. Drag to look around.",
      },
    },
  ],

  edges: [
    { from: "b_intro", to: "b_kids" },
    { from: "b_kids", to: "b_behind" },
    { from: "b_behind", to: "b_behind_close" },
    { from: "b_behind_close", to: "b_shoulder" },
    { from: "b_shoulder", to: "b_zoom" },
    { from: "b_zoom", to: "b_ipad" },
    { from: "b_ipad", to: "b_walk" },
  ],
};
